import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { useRole } from "../context/RoleContext";
import { listEmailLog, formatTimestamp } from "../lib/notifications";
import EmptyState from "../components/EmptyState";

const WINDOWS = [
  { v: "24h", label: "Last 24 hours" },
  { v: "7d", label: "Last 7 days" },
  { v: "all", label: "All time" },
];

export default function AdminEmailLog() {
  const { user } = useRole();
  const [window, setWindow] = useState("all");
  const [eventType, setEventType] = useState("all");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setData(null);
    try {
      const d = await listEmailLog(user.id, { window, eventType });
      setData(d);
    } catch {
      setData({ items: [], total: 0, event_types: [] });
    }
  }, [user?.id, window, eventType]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="animate-fade-in-up" data-testid="admin-email-log-page">
      <div>
        <div className="flex items-center gap-3">
          <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
          <span className="wn-label" style={{ color: "#8F3C23" }}>Platform Admin · Simulated Email Log</span>
        </div>
        <h1
          className="mt-3"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: "clamp(26px, 3.6vw, 32px)",
            fontWeight: 600,
            color: "#1B1F4B",
          }}
        >
          Simulated email log.
        </h1>
        <p style={{ color: "#6C6B85", fontSize: 14, marginTop: 4, maxWidth: 620 }}>
          Every email the platform <em style={{ fontFamily: '"Newsreader", Georgia, serif' }}>would have sent</em> is recorded here for demo transparency. No real email is dispatched in this Proof of Concept.
        </p>
      </div>

      <div className="mt-6 wn-card" style={{ padding: 16 }} data-testid="admin-email-log-filters">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="wn-label block">Time window</label>
            <select
              className="wn-input"
              value={window}
              onChange={(e) => setWindow(e.target.value)}
              data-testid="admin-email-window"
            >
              {WINDOWS.map((w) => (
                <option key={w.v} value={w.v}>{w.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="wn-label block">Event type</label>
            <select
              className="wn-input"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              data-testid="admin-email-type"
            >
              <option value="all">All event types</option>
              {(data?.event_types || []).map((k) => (
                <option key={k} value={k}>{prettyKind(k)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-5">
        {data === null && (
          <div style={{ color: "#6C6B85", fontStyle: "italic" }}>Loading log…</div>
        )}
        {data && data.items.length === 0 && (
          <EmptyState
            title={eventType !== "all" || window !== "all" ? "No emails match these filters." : "No simulated emails yet."}
            body={
              eventType !== "all" || window !== "all"
                ? "Widen the time window or pick a different event type."
                : "Architect approvals, design reviews, and consultation activity will appear here as they happen."
            }
            actionLabel={eventType !== "all" || window !== "all" ? "Clear filters" : null}
            onAction={() => {
              setWindow("all");
              setEventType("all");
            }}
          />
        )}
        {data && data.items.length > 0 && (
          <div
            className="wn-card"
            style={{ padding: 0, overflow: "hidden" }}
            data-testid="admin-email-log-table-card"
          >
            <table className="w-full" style={{ borderCollapse: "collapse" }} data-testid="admin-email-log-table">
              <thead>
                <tr style={{ background: "#1B1F4B", color: "#FBFAFD" }}>
                  <th style={th}>Sent</th>
                  <th style={th}>Event</th>
                  <th style={th}>Recipient</th>
                  <th style={th}>Subject</th>
                  <th style={th}>Preview</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e, i) => (
                  <tr
                    key={e.id}
                    style={{ background: i % 2 === 0 ? "#FBFAFD" : "#F8F7FB" }}
                    data-testid={`admin-email-row-${e.id}`}
                  >
                    <td style={{ ...td, fontWeight: 600 }} className="tabular-nums">
                      {formatTimestamp(e.sent_at)}
                    </td>
                    <td style={td}>
                      <div className="wn-label" style={{ letterSpacing: "0.08em", color: "#3A5563" }}>
                        {prettyKind(e.kind)}
                      </div>
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: "#1B1F4B" }}>{e.to_name || "—"}</div>
                      <div className="tabular-nums" style={{ color: "#6C6B85", fontSize: 11 }}>
                        {e.to || "—"}
                      </div>
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>
                      <Link
                        to={`/admin/email-log/${encodeURIComponent(e.id)}`}
                        data-testid={`admin-email-link-${e.id}`}
                        style={{ color: "#1B1F4B", textDecoration: "underline", textUnderlineOffset: 3 }}
                      >
                        {e.subject || "—"}
                      </Link>
                    </td>
                    <td style={{ ...td, color: "#272636" }}>
                      <div style={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {e.body || "—"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div
              style={{
                padding: "10px 14px",
                borderTop: "1px solid #D9D8E4",
                color: "#6C6B85",
                fontSize: 12,
                background: "#FBFAFD",
              }}
            >
              <Mail size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} />
              Showing <span className="tabular-nums" style={{ color: "#272636", fontWeight: 700 }}>{data.items.length}</span> {data.items.length === 1 ? "entry" : "entries"}.
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const th = { textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" };
const td = { padding: "10px 14px", fontSize: 12.5, color: "#272636", borderTop: "1px solid #D9D8E4", verticalAlign: "top" };

function prettyKind(k) {
  if (!k) return "—";
  return k.replace(/_/g, " ").replace(/\./g, " → ").replace(/\b\w/g, (c) => c.toUpperCase());
}
